import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { Banknote, Check, CreditCard, MoreHorizontal } from "lucide-react";
import React from "react";
import type { AccountSelectionMode } from "../types";

interface AccountSelectItemProps {
  type: string;
  name: string;
  accountNumber: string;
  currency: string;
  balance?: number;
  showBalance?: boolean;
  selected?: boolean;
  onClick?: () => void;
  selectionMode?: AccountSelectionMode;
}

const formatMoney = (amount: number) => {
  return amount.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,");
};

const AccountSelectItem: React.FC<AccountSelectItemProps> = ({
  type,
  name,
  accountNumber,
  currency,
  balance,
  showBalance = true,
  selected,
  onClick,
  selectionMode = "multi",
}) => {
  const isAllMode = selectionMode === "all";
  const isClickable = !isAllMode;

  const AccountIcon = type === "credit" ? CreditCard : Banknote;

  return (
    <div
      onClick={isClickable ? onClick : undefined}
      className={cn(
        "ph-no-capture relative rounded-xl border-2 px-3 py-3 transition-all duration-150",
        isClickable && "cursor-pointer active:scale-[0.98]",
        selected
          ? "border-bank-primary bg-bank-surface/30"
          : "border-border/70 bg-card hover:border-border hover:bg-muted/30",
      )}
    >
      <div className="flex items-center gap-2.5">
        {/* Icon */}
        <div
          className={cn(
            "h-11 w-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors duration-150",
            selected ? "bg-bank-primary/10" : "bg-muted",
          )}
        >
          <AccountIcon
            className={cn(
              "h-5 w-5 transition-colors duration-150",
              selected ? "text-bank-primary" : "text-muted-foreground",
            )}
          />
        </div>

        {/* Name and pills */}
        <div className="flex-1 min-w-0">
          <p className="font-medium text-foreground text-[15px] leading-tight">
            {name}
          </p>
          <div className="flex items-center gap-1.5 mt-1.5">
            <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full border border-border text-xs text-muted-foreground">
              <MoreHorizontal className="h-3 w-3 opacity-50" />
              {accountNumber}
            </span>
            {showBalance && balance !== undefined ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-border text-xs text-muted-foreground">
                {currency} ${formatMoney(balance)}
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-border text-xs text-muted-foreground">
                {currency}
              </span>
            )}
          </div>
        </div>

        {/* Selection indicator */}
        {!isAllMode && (
          <div
            className={cn(
              "h-5 w-5 border-2 flex items-center justify-center flex-shrink-0 transition-all duration-150",
              selectionMode === "single" ? "rounded-full" : "rounded",
              selected
                ? "border-bank-primary bg-bank-primary"
                : "border-border bg-background",
            )}
          >
            {selected && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              >
                <Check className="h-3 w-3 text-bank-primary-foreground" />
              </motion.div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountSelectItem;
